import { create } from 'zustand';
import { fetchDailyOverview, appendToPurchaseOrder } from '@/api/onderaannemers';
import type {
  DailyOverviewResponse,
  AppendPurchaseOrderResult,
  PurchaseOrderConflict,
  AppendPurchaseOrderConflictCode,
  TierBand,
} from '@/api/onderaannemers';
import { ApiError } from '@/api/errors';

// Today as YYYY-MM-DD using local components — never toISOString (CET shift).
function todayLocal(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

// Outcome of an appendSelected call, handed back to the cockpit so it can render
// the right Dutch messaging. Selection is only cleared on a clean/partial success.
export type AppendOutcome =
  | { status: 'ok'; result: AppendPurchaseOrderResult }
  | {
      status: 'conflict';
      code: AppendPurchaseOrderConflictCode;
      conflicts: PurchaseOrderConflict[];
      // Present on the "all selected already on a PO" and CONCURRENT_APPEND cases.
      message?: string;
    }
  | {
      // The batch contained collectors whose fase is marked "Collectoren geleverd"
      // (client-placed them) and the request didn't carry acknowledgedGeleverd:true.
      // The cockpit must show a confirm dialog then retry with acknowledgedGeleverd:true.
      status: 'geleverd_confirm';
      flagged: { id: number; name: string; faseName: string | null }[];
    }
  | { status: 'error'; message: string };

interface OnderaannemersState {
  // Daily overview state (OA → day → collectors)
  overviewData: DailyOverviewResponse | null;
  overviewLoading: boolean;
  overviewError: string | null;
  overviewDateFrom: string; // YYYY-MM-DD — fetch collectors with photos from this day on
  hidePO: boolean;          // hide collectors that already have a PO (client-side)
  fetchDailyOverview: () => Promise<void>;
  setOverviewDateFrom: (date: string) => void;
  toggleHidePO: () => void;

  // Per-OA collector selection for the rolling-PO append cockpit.
  // installerId → array of selected collectorIds (plain arrays keep Zustand
  // updates trivially immutable and JSON-serialisable).
  selection: Record<number, number[]>;
  // Per-OA "al geplaatst" marks (collectors that get the minprijs deduction).
  // Always a SUBSET of `selection[installerId]` — deselecting a collector prunes it
  // here too, so the server's "geplaatste ⊆ selectie" guard can never trip.
  geplaatstSelection: Record<number, number[]>;
  // Per-OA meerwerk selection — installerId → array of selected meerwerkIds. Selected
  // inline in the cockpit body, exactly like collectors (no longer picked in the modal).
  meerwerkSelection: Record<number, number[]>;
  appendingInstallerId: number | null; // OA whose append request is in flight
  toggleCollectorSelection: (installerId: number, collectorId: number) => void;
  toggleGeplaatst: (installerId: number, collectorId: number) => void;
  toggleMeerwerkSelection: (installerId: number, meerwerkId: number) => void;
  clearSelection: (installerId: number) => void;
  appendSelected: (
    installerId: number,
    opts?: {
      allowSkip?: boolean;
      tierOverrides?: Record<number, TierBand>;
      customSystemRates?: Record<string, number>;
      // Forward the user's explicit acknowledgement that client-placed collectors
      // should still be billed. Clears the COLLECTOR_LIKELY_PREPLACED 409.
      acknowledgedGeleverd?: boolean;
    },
  ) => Promise<AppendOutcome>;
}

export const useOnderaannemersStore = create<OnderaannemersState>((set, get) => ({
  overviewData: null,
  overviewLoading: false,
  overviewError: null,
  overviewDateFrom: todayLocal(),
  hidePO: true,
  selection: {},
  geplaatstSelection: {},
  meerwerkSelection: {},
  appendingInstallerId: null,

  fetchDailyOverview: async () => {
    set({ overviewLoading: true, overviewError: null });
    try {
      const data = await fetchDailyOverview(get().overviewDateFrom);
      set({ overviewData: data, overviewLoading: false });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Dagoverzicht laden mislukt';
      set({ overviewError: message, overviewLoading: false });
    }
  },

  setOverviewDateFrom: (date: string) => {
    if (!date || date === get().overviewDateFrom) return;
    set({ overviewDateFrom: date });
    get().fetchDailyOverview();
  },

  toggleHidePO: () => set((s) => ({ hidePO: !s.hidePO })),

  toggleCollectorSelection: (installerId, collectorId) =>
    set((s) => {
      const current = s.selection[installerId] ?? [];
      const isRemoving = current.includes(collectorId);
      const next = isRemoving
        ? current.filter((id) => id !== collectorId)
        : [...current, collectorId];
      const selection = { ...s.selection };
      if (next.length === 0) delete selection[installerId];
      else selection[installerId] = next;

      // Deselecting a collector must drop any "al geplaatst" mark on it (geplaatst ⊆ selectie).
      const result: Partial<OnderaannemersState> = { selection };
      if (isRemoving) {
        const geplaatst = s.geplaatstSelection[installerId];
        if (geplaatst?.includes(collectorId)) {
          const prunedGeplaatst = geplaatst.filter((id) => id !== collectorId);
          const geplaatstSelection = { ...s.geplaatstSelection };
          if (prunedGeplaatst.length === 0) delete geplaatstSelection[installerId];
          else geplaatstSelection[installerId] = prunedGeplaatst;
          result.geplaatstSelection = geplaatstSelection;
        }
      }
      return result;
    }),

  toggleGeplaatst: (installerId, collectorId) =>
    set((s) => {
      // Only collectors that are actually selected can be marked "al geplaatst".
      const selected = s.selection[installerId] ?? [];
      if (!selected.includes(collectorId)) return s;
      const current = s.geplaatstSelection[installerId] ?? [];
      const next = current.includes(collectorId)
        ? current.filter((id) => id !== collectorId)
        : [...current, collectorId];
      const geplaatstSelection = { ...s.geplaatstSelection };
      if (next.length === 0) delete geplaatstSelection[installerId];
      else geplaatstSelection[installerId] = next;
      return { geplaatstSelection };
    }),

  toggleMeerwerkSelection: (installerId, meerwerkId) =>
    set((s) => {
      const current = s.meerwerkSelection[installerId] ?? [];
      const next = current.includes(meerwerkId)
        ? current.filter((id) => id !== meerwerkId)
        : [...current, meerwerkId];
      const meerwerkSelection = { ...s.meerwerkSelection };
      if (next.length === 0) delete meerwerkSelection[installerId];
      else meerwerkSelection[installerId] = next;
      return { meerwerkSelection };
    }),

  clearSelection: (installerId) =>
    set((s) => {
      const hasSel = installerId in s.selection;
      const hasGeplaatst = installerId in s.geplaatstSelection;
      const hasMeerwerk = installerId in s.meerwerkSelection;
      if (!hasSel && !hasGeplaatst && !hasMeerwerk) return s;
      const selection = { ...s.selection };
      const geplaatstSelection = { ...s.geplaatstSelection };
      const meerwerkSelection = { ...s.meerwerkSelection };
      delete selection[installerId];
      delete geplaatstSelection[installerId];
      delete meerwerkSelection[installerId];
      return { selection, geplaatstSelection, meerwerkSelection };
    }),

  appendSelected: async (installerId, opts) => {
    const collectorIds = get().selection[installerId] ?? [];
    const meerwerkIds = get().meerwerkSelection[installerId] ?? [];
    if (collectorIds.length === 0 && meerwerkIds.length === 0) {
      return { status: 'error', message: 'Niets geselecteerd om toe te voegen.' };
    }

    // Keep only marks that are still in the selection (defensive — the toggles
    // already maintain this, but allowSkip retries shouldn't resurrect stale marks).
    const selectedSet = new Set(collectorIds);
    const geplaatstCollectorIds = (get().geplaatstSelection[installerId] ?? []).filter((id) =>
      selectedSet.has(id),
    );

    set({ appendingInstallerId: installerId });
    try {
      const result = await appendToPurchaseOrder({
        installerId,
        collectorIds,
        geplaatstCollectorIds,
        allowSkip: opts?.allowSkip ?? false,
        tierOverrides: opts?.tierOverrides,
        customSystemRates: opts?.customSystemRates,
        ...(opts?.acknowledgedGeleverd ? { acknowledgedGeleverd: true } : {}),
        ...(meerwerkIds.length > 0 ? { meerwerkIds } : {}),
      });
      // Success (200/207). Refresh the overview so PO badges update, then clear
      // this OA's selection.
      await get().fetchDailyOverview();
      get().clearSelection(installerId);
      return { status: 'ok', result };
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.code === 'COLLECTOR_ALREADY_ON_PO' || err.code === 'CONCURRENT_APPEND') {
          const body = (err.body ?? {}) as { conflicts?: PurchaseOrderConflict[]; message?: string };
          // Only the all-conflict + CONCURRENT_APPEND variants carry a `message` in
          // the body. The partial COLLECTOR_ALREADY_ON_PO 409 has none — leaving
          // `message` undefined is the signal the cockpit uses to offer "Ga door
          // zonder deze N". Don't substitute the ApiError's generic fallback here.
          return {
            status: 'conflict',
            code: err.code,
            conflicts: Array.isArray(body.conflicts) ? body.conflicts : [],
            ...(typeof body.message === 'string' ? { message: body.message } : {}),
          };
        }
        if (err.code === 'COLLECTOR_LIKELY_PREPLACED') {
          // err.body is the full 409 payload: { ok, code, message, body: { flagged: [...] } }.
          // The cockpit will show window.confirm and retry with acknowledgedGeleverd:true.
          type PreplacedBody = { body?: { flagged?: { id: number; name: string; faseName: string | null }[] } };
          const flagged = (err.body as PreplacedBody | undefined)?.body?.flagged ?? [];
          return { status: 'geleverd_confirm', flagged };
        }
        // 400s (COLLECTOR_NOT_FOUND / COLLECTOR_WITHOUT_PROJECT / VALIDATION_ERROR)
        // and any other server error carry a Dutch message.
        return { status: 'error', message: err.message };
      }
      return {
        status: 'error',
        message: err instanceof Error ? err.message : 'Toevoegen aan PO mislukt',
      };
    } finally {
      set({ appendingInstallerId: null });
    }
  },
}));
